import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { faAngleDown, faAngleUp, faChevronLeft, faChevronRight, faFileAlt } from '@fortawesome/pro-solid-svg-icons';
import { Spinner } from 'react-bootstrap';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { FileModel } from 'models';

import { useValidatedParam } from 'core/hooks';
import { Button, ButtonVariants, Icon } from 'core/ui';

import { useApiClient, useQueryFilesByExamId } from 'features/api';
import { DocumentCategories, ExamStatus } from 'features/exam/constants';
import { FileInfoBar, FileViewer } from 'features/file';

import { FileSelectSidebar } from './FileSelectSidebar';
import { FileTimeline } from './FileTimeline';
import { ImgProxy } from './ImgProxy';

// ADD SESSION STORAGE KEY
const GALLERY_SCROLL_SESSION_KEY = 'fileViewerGalleryScrollPos';

/** Exam statuses that trigger an initial selection for currentFile to be set for the first file that is marked as in the "Final Report" category. */
const AUTO_SELECT_FILE_FINAL_REPORT_STATUSES: string[] = [ExamStatus.READ.name, ExamStatus.COMPLETE.name];

interface FileViewerPageProps {
  /** Optional files to display instead of fetching by examId */
  files?: FileModel[];
  /** Optional pre-selected file */
  selectedFile?: FileModel | null;
  examId?: number;
  showSidebar?: boolean;
  showInfobar?: boolean;
  showEditBtn?: boolean;
  showDeleteBtn?: boolean;
  onDeleteFile?: (fileId: string | number) => Promise<boolean>;
  onFileDeleted?: (fileId: string | number) => void;
  showNavButtons?: boolean;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  onFileSelect?: (file: FileModel) => void;
  /** Renders the thumbnail gallery at the bottom */
  showGallery?: boolean; // Renamed from showDetail
  /** Renders the timeline view at the bottom */
  showTimeline?: boolean;
}

// --- Styled Components for Gallery Section --- RENAMED
// Renamed from DetailSectionWrapper
const GallerySectionWrapper = styled.div`
  grid-area: gallery; // Changed from detail
  overflow: hidden;
  background-color: #f8f9fa;
  padding-top: 0.75rem; // Add padding above carousel
  border-top: 1px solid #e0e0e0; // Add border above carousel
`;

const ThumbnailCarousel = styled.div`
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0 0.75rem 0.75rem; // Restore padding

  /* Simple scrollbar styling */
  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 3px;
  }
`;

const ThumbnailItem = styled.div<{ $isSelected: boolean }>`
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  border: 2px solid ${({ $isSelected, theme }) => ($isSelected ? theme.colors.primary[500] : 'transparent')};
  border-radius: 4px;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  background-color: #e9ecef; // Placeholder bg

  img,
  .placeholder-icon {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  .placeholder-icon {
    font-size: 24px;
    color: #adb5bd;
    text-align: center;
    padding-top: 15px; /* Adjust as needed */
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary[300]};
  }
`;

// --- Add Styled Components for Timeline Section ---
const TimelineSectionWrapper = styled.div`
  grid-area: timeline;
  overflow: hidden; // Let internal component handle scroll
  background-color: #f0f0f0; // Example background
`;

export const FileViewerPage = memo(
  ({
    files: propFiles,
    selectedFile: initialSelectedFile,
    examId,
    showSidebar = false,
    showInfobar = false,
    showEditBtn = false,
    showDeleteBtn = false,
    onDeleteFile,
    onFileDeleted,
    showNavButtons = false,
    onNavigatePrevious: onNavigatePreviousProp,
    onNavigateNext: onNavigateNextProp,
    onFileSelect: onFileSelectProp,
    showGallery = false,
    showTimeline = false,
  }: FileViewerPageProps) => {
    const [queryParams] = useSearchParams();
    const { fileId } = useParams<{ fileId: string }>();
    const feature = queryParams.get('feature') ?? null;
    const status = queryParams.get('status') ?? ExamStatus.NEW.value;
    const apiClient = useApiClient();

    // ADD REF FOR CAROUSEL
    const galleryRef = useRef<HTMLDivElement>(null);

    const examIdFromUrl = useValidatedParam('examId', 'number', false);
    const effectiveExamId = examId ?? (examIdFromUrl ? examIdFromUrl : null);

    // Only query API if we have a valid examId AND (no prop files were provided OR prop files is an empty array)
    const shouldFetchFiles =
      effectiveExamId !== undefined && effectiveExamId !== null && effectiveExamId > 0 && (!propFiles || (Array.isArray(propFiles) && propFiles.length === 0));

    // Use a type assertion to work with the function overload
    const queryId = shouldFetchFiles ? effectiveExamId : (null as unknown as number);
    const { data: fetchedFiles, isLoading, error, refetch } = useQueryFilesByExamId(queryId);

    // Compute filesSource based on whether props or fetched data should be used
    const filesSource = useMemo(() => propFiles ?? fetchedFiles, [propFiles, fetchedFiles]);

    // Compute filteredFiles based on the feature type and filesSource
    const filteredFiles = useMemo(() => {
      if (!filesSource) {
        return null; // Return null if no source is available yet
      }
      // Skip filtering if loading from API or error occurred (unless propFiles were provided)
      if (shouldFetchFiles && isLoading && !propFiles) return null;
      if (shouldFetchFiles && error && !propFiles) return null;

      switch (feature) {
        case 'exams':
          return filesSource.filter((file) => file.categoryId === DocumentCategories.EXAM.value || file.fileType === 'LINK') ?? [];
        case 'attachments':
          return filesSource.filter((file) => file.categoryId !== DocumentCategories.EXAM.value && file.fileType !== 'LINK') ?? [];
        default:
          // If no feature specified, use all files from the source
          return filesSource ?? null;
      }
    }, [feature, filesSource, isLoading, error, propFiles, shouldFetchFiles]);

    // Introduce localFiles to manage the file list derived from filteredFiles
    const [localFiles, setLocalFiles] = useState<FileModel[] | null>(null);

    // Sync localFiles with filteredFiles when filteredFiles changes
    useEffect(() => {
      setLocalFiles(filteredFiles);
    }, [filteredFiles]);

    // Revised useState for currentFile: Prioritize initialSelectedFile if valid in initial propFiles, else null.
    const [currentFile, setCurrentFile] = useState<FileModel | null>(() => {
      if (initialSelectedFile && propFiles?.some((f) => f.id === initialSelectedFile.id)) {
        return initialSelectedFile;
      }
      return null;
    });

    // --- Refactored useEffect Hooks ---

    // Effect for setting the *initial* file or re-selecting if current becomes invalid
    useEffect(() => {
      // Only run if localFiles are populated
      if (localFiles && localFiles.length > 0) {
        let fileToSelect: FileModel | null = null;

        // Priority 1: If fileId is in URL, select that file
        if (fileId) {
          const fileFromUrl = localFiles.find((f) => f.id.toString() === fileId);
          if (fileFromUrl) {
            fileToSelect = fileFromUrl;
          }
        }
        // Priority 2: If currentFile is set but NO LONGER exists in localFiles, reselect.
        else if (currentFile !== null && !localFiles.some((f) => f.id === currentFile.id)) {
          fileToSelect = selectCurrentFile(localFiles, status);
        }
        // Priority 3: If currentFile is NULL (initial load or list became non-empty), select initial.
        else if (currentFile === null) {
          if (initialSelectedFile && localFiles.some((f) => f.id === initialSelectedFile.id)) {
            fileToSelect = initialSelectedFile;
          } else {
            fileToSelect = selectCurrentFile(localFiles, status);
          }
        }

        // If fileToSelect was determined, update the state
        if (fileToSelect && fileToSelect.id !== currentFile?.id) {
          setCurrentFile(fileToSelect);
        }
      } else if (localFiles && localFiles.length === 0 && currentFile !== null) {
        setCurrentFile(null);
      }
    }, [localFiles, status, fileId, currentFile, initialSelectedFile]);

    // Effect to react ONLY to external changes of initialSelectedFile prop
    useEffect(() => {
      // console.log('External initialSelectedFile effect ran. Prop:', initialSelectedFile?.id, 'Current:', currentFile?.id); // Debug log
      if (initialSelectedFile && initialSelectedFile.id !== currentFile?.id) {
        const fileExistsLocally = localFiles?.some((f) => f.id === initialSelectedFile.id);
        if (fileExistsLocally) {
          // console.log('Updating current file due to external prop change:', initialSelectedFile.id); // Debug log
          // If the prop changes to a *different* valid file, update internal state
          setCurrentFile(initialSelectedFile);
        }
      }
      // This effect specifically syncs with the prop.
    }, [initialSelectedFile, localFiles]); // Depends on prop and localFiles list

    // --- End Refactored Hooks ---

    // Delete file using either provided or default implementation
    const deleteFile = useCallback(
      async (fileId: string | number) => {
        try {
          if (onDeleteFile) {
            const success = await onDeleteFile(fileId);
            if (success && onFileDeleted) {
              onFileDeleted(fileId); // Call internal handler first if provided
            }
            return success;
          } else {
            // Default implementation if no onDeleteFile provided
            await apiClient.filesClient.deleteFile(typeof fileId === 'string' ? parseInt(fileId, 10) : fileId, 'msal-required');
            // Call internal handler after default deletion
            if (onFileDeleted) {
              onFileDeleted(fileId);
            }
            return true; // Assume success if API call doesn't throw
          }
        } catch (error) {
          console.error('Error deleting file:', error);
          return false;
        }
      },
      [apiClient, onDeleteFile, onFileDeleted], // Added onFileDeleted dependency
    );

    // Update local file list and current selection immediately on deletion
    // Renamed internal handler to avoid conflict with prop name
    const handleLocalFileDeletion = useCallback(
      (deletedFileId: string | number) => {
        console.log('Handling local file deletion:', deletedFileId);

        // Optimistically update the local file list
        if (localFiles) {
          const updatedFiles = localFiles.filter((f) => f.id !== deletedFileId);
          setLocalFiles(updatedFiles); // Update local state

          // Reselect current file if the deleted one was open
          if (currentFile?.id === deletedFileId) {
            if (updatedFiles.length > 0) {
              // Try to select the next file, or the previous one if it was the last
              const deletedIndex = localFiles.findIndex((f) => f.id === deletedFileId);
              const newIndex = deletedIndex >= updatedFiles.length ? updatedFiles.length - 1 : deletedIndex;
              setCurrentFile(updatedFiles[newIndex]);
            } else {
              setCurrentFile(null); // No files left
            }
          }
          // No need to change currentFile if a different file was deleted
        }

        // Refetch to keep in sync with backend data if files were fetched via API
        if (shouldFetchFiles && effectiveExamId && effectiveExamId > 0) {
          refetch();
        }
      },
      [localFiles, currentFile, effectiveExamId, refetch, shouldFetchFiles],
    );

    // Use localFiles for the sidebar so changes reflect immediately
    const filesForSidebar = useMemo(() => {
      return (localFiles ?? []).map((file) => ({
        id: file.id,
        thumbnailUrl: file.thumbnailUrl ?? null,
        fileName: file.fileName,
        modified: file.modified ?? null,
        categoryId: file.categoryId ?? null,
        viewerId: file.viewerId ?? null,
      }));
    }, [localFiles]);

    const timelineFileIds = useMemo(() => localFiles?.map((f) => f.id).filter((id): id is number => typeof id === 'number') ?? [], [localFiles]);

    const selectedFileItem = useMemo(() => {
      if (!currentFile) return null;
      return {
        id: currentFile.id,
        fileName: currentFile.fileName,
        modified: currentFile.modified ?? null,
        categoryId: currentFile.categoryId ?? null,
        thumbnailUrl: currentFile.thumbnailUrl ?? null,
        viewerId: currentFile.viewerId ?? null,
      };
    }, [currentFile]);

    // Calculate current index and navigation possibility
    const currentIndex = useMemo(() => {
      if (!currentFile || !localFiles) return -1;
      return localFiles.findIndex((f) => f.id === currentFile.id);
    }, [currentFile, localFiles]);

    const canNavigatePrevious = useMemo(() => currentIndex > 0, [currentIndex]);
    const canNavigateNext = useMemo(() => localFiles != null && currentIndex < localFiles.length - 1, [currentIndex, localFiles]);

    // Internal handler for navigating to the previous file
    const handleNavigatePrevious = useCallback(() => {
      if (onNavigatePreviousProp) {
        onNavigatePreviousProp();
        return;
      }
      const currentIndex = localFiles?.findIndex((f) => f.id === currentFile?.id) ?? -1;
      if (localFiles && currentIndex > 0) {
        const previousFile = localFiles[currentIndex - 1];
        if (onFileSelectProp) {
          onFileSelectProp(previousFile);
        } else {
          setCurrentFile(previousFile);
        }
      }
    }, [currentFile, localFiles, onNavigatePreviousProp, onFileSelectProp]);

    // Internal handler for navigating to the next file
    const handleNavigateNext = useCallback(() => {
      if (onNavigateNextProp) {
        onNavigateNextProp();
        return;
      }
      const currentIndex = localFiles?.findIndex((f) => f.id === currentFile?.id) ?? -1;
      if (localFiles && currentIndex !== -1 && currentIndex < localFiles.length - 1) {
        const nextFile = localFiles[currentIndex + 1];
        if (onFileSelectProp) {
          onFileSelectProp(nextFile);
        } else {
          setCurrentFile(nextFile);
        }
      }
    }, [currentFile, localFiles, onNavigateNextProp, onFileSelectProp]);

    // --- EFFECTS for Scroll Persistence ---
    // Restore scroll position on mount
    useEffect(() => {
      const savedScrollPos = sessionStorage.getItem(GALLERY_SCROLL_SESSION_KEY);
      if (savedScrollPos && galleryRef.current) {
        // Use timeout to ensure element is ready
        setTimeout(() => {
          if (galleryRef.current) {
            galleryRef.current.scrollLeft = parseInt(savedScrollPos, 10);
          }
        }, 0);
      }
      // Clear the stored position after restoring
      // sessionStorage.removeItem(GALLERY_SCROLL_SESSION_KEY);
    }, []); // Run only on mount

    // Save scroll position on unmount or before navigating away
    useEffect(() => {
      const saveScrollPosition = () => {
        if (galleryRef.current) {
          sessionStorage.setItem(GALLERY_SCROLL_SESSION_KEY, galleryRef.current.scrollLeft.toString());
        }
      };

      // Save before unload
      window.addEventListener('beforeunload', saveScrollPosition);

      // Cleanup function to save when component unmounts
      return () => {
        saveScrollPosition();
        window.removeEventListener('beforeunload', saveScrollPosition);
      };
    }, []); // Run only once to set up listeners
    // -------------------------------------

    // --- Render Logic ---
    const renderContent = () => {
      if (shouldFetchFiles && isLoading && !localFiles) {
        return (
          <StyledSpinnerContainer>
            <Spinner />
            <div>Loading files from exam ID: {effectiveExamId}...</div>
          </StyledSpinnerContainer>
        );
      }
      if (error && !localFiles) {
        return (
          <StyledSpinnerContainer>
            <div style={{ color: 'red' }}>Error loading files: {String(error)}</div>
          </StyledSpinnerContainer>
        );
      }
      if (!localFiles && propFiles && propFiles.length > 0) {
        return (
          <StyledSpinnerContainer>
            <Spinner />
            <div>Processing {propFiles.length} files...</div>
          </StyledSpinnerContainer>
        );
      }
      if (localFiles && localFiles.length === 0) {
        return (
          <StyledSpinnerContainer>
            <div>No files found</div>
          </StyledSpinnerContainer>
        );
      }
      if (localFiles && !currentFile) {
        return (
          <StyledSpinnerContainer>
            <div>No file selected. Please select a file.</div>
          </StyledSpinnerContainer>
        );
      }
      if (currentFile) {
        return (
          <>
            {showNavButtons && (
              <>
                <NavArrowButton onClick={handleNavigatePrevious} disabled={!canNavigatePrevious} title="Previous File" $position="left">
                  <Icon icon={faChevronLeft} />
                </NavArrowButton>
                <NavArrowButton onClick={handleNavigateNext} disabled={!canNavigateNext} title="Next File" $position="right">
                  <Icon icon={faChevronRight} />
                </NavArrowButton>
              </>
            )}
            <FileViewer file={currentFile} overrideMultiMonitor />
          </>
        );
      }
      // Default fallback (should ideally not be reached often with above logic)
      return (
        <StyledSpinnerContainer>
          <div>No files available</div>
        </StyledSpinnerContainer>
      );
    };

    // --- Gallery Section Rendering --- // RENAMED from renderDetailSection
    const renderGallerySection = () => {
      // Remove the check for currentFile and localFiles length
      // Always render the carousel container, map handles empty list

      // ADD REF TO CAROUSEL
      return (
        <ThumbnailCarousel ref={galleryRef}>
          {(localFiles ?? []).map((file) => (
            <ThumbnailItem key={file.id} onClick={() => onFileSelectProp?.(file)} $isSelected={currentFile?.id === file.id} title={file.fileName}>
              <ImgProxy fileId={file.id} thumbnailUrl={file.thumbnailUrl ?? undefined} dateModified={file.modified ?? undefined} isReadOnly={true} />
            </ThumbnailItem>
          ))}
        </ThumbnailCarousel>
      );
    };

    // --- Timeline Section Rendering ---
    const renderTimelineSection = () => {
      // Prepare file IDs from the current local files list
      // Filter for numbers and ensure the result is number[]
      const localFileIds = timelineFileIds;

      // Only render if showTimeline is true and we have either a valid examId or valid fileIds
      if (!showTimeline || (effectiveExamId == null && localFileIds.length === 0)) {
        return null;
      }

      // Pass both examId and fileIds. The component will prioritize.
      return <FileTimeline examId={effectiveExamId} fileIds={localFileIds} />;
    };

    return (
      <>
        <StyledContainer
          className="file-viewer-page"
          $showInfobar={showInfobar}
          $showSidebar={showSidebar}
          $showGallery={showGallery}
          $showTimeline={showTimeline} // Pass down the new prop
        >
          {showInfobar && (
            <div className="file-info-bar">
              <FileInfoBar file={currentFile} $defaultCollapsed={false} />
            </div>
          )}

          <StyledViewerContainer className="viewer-area">{renderContent()}</StyledViewerContainer>

          {showSidebar && (
            <FileSelectSidebar
              className="file-select-sidebar"
              files={filesForSidebar}
              selectedFile={selectedFileItem}
              handleFileSelect={(file) => file && onFileSelectProp?.(file)}
              showDeleteBtn={showDeleteBtn}
              showEditBtn={showEditBtn}
              examId={effectiveExamId && effectiveExamId > 0 ? effectiveExamId.toString() : undefined}
              hideUploadButton={true}
              defaultCollapsed={false}
              onFileDeleted={handleLocalFileDeletion}
              onDeleteFile={deleteFile}
            />
          )}
          {/* Render gallery based on showGallery prop */}
          {showGallery && <GallerySectionWrapper className="gallery-section-wrapper">{renderGallerySection()}</GallerySectionWrapper>}

          {/* Render timeline based on showTimeline prop */}
          {showTimeline && <TimelineSectionWrapper className="timeline-section-wrapper">{renderTimelineSection()}</TimelineSectionWrapper>}
        </StyledContainer>
      </>
    );
  },
);

FileViewerPage.displayName = 'FileViewerPage';

// Helper function to select the initial file based on status or default to first
function selectCurrentFile(files: FileModel[] | null, status: string): FileModel | null {
  // Removed console.log('selectCurrentFile');
  if (files == null || files.length === 0) return null; // Return null if no files
  if (AUTO_SELECT_FILE_FINAL_REPORT_STATUSES.includes(status)) {
    // Try finding 'FINAL_REPORT' first
    const finalReport = files.find((file) => file.categoryId === DocumentCategories.FINAL_REPORT.value);
    if (finalReport) return finalReport;
  }
  // Default to the first file in the list if no specific logic applies or finds a match
  return files[0];
}

// Forward declare StyledViewerContainer type for use in StyledContainerProps if needed (Alternative to moving)
// type StyledViewerContainerType = typeof StyledViewerContainer;

// Moved StyledContainer and its props interface definition to the bottom

interface StyledContainerProps {
  $showInfobar: boolean;
  $showSidebar: boolean;
  $showGallery: boolean; // Renamed from $showDetail
  $showTimeline: boolean; // Add prop for timeline
}

const StyledContainer = styled.div<StyledContainerProps>`
  display: grid;
  overflow: hidden;
  /* Define grid areas */
  grid-template-areas: ${({ $showInfobar, $showSidebar, $showGallery, $showTimeline }) => {
    let areas = '';
    // Base layout row(s)
    if ($showInfobar && $showSidebar) areas = '"infobar viewer sidebar"\n';
    else if ($showInfobar) areas = '"infobar viewer viewer"\n';
    else if ($showSidebar) areas = '"viewer viewer sidebar"\n';
    else areas = '"viewer viewer viewer" ';

    // Add gallery row if shown
    if ($showGallery) {
      areas = areas.trim() + '\n"gallery gallery gallery"';
    }
    // Add timeline row if shown
    if ($showTimeline) {
      areas = areas.trim() + '\n"timeline timeline timeline"';
    }

    return areas.trim();
  }};

  /* Define columns based on visible sidebars */
  grid-template-columns: ${({ $showInfobar, $showSidebar }) => {
    const infobarCol = $showInfobar ? 'min-content' : '';
    const viewerCol = '1fr';
    const sidebarCol = $showSidebar ? 'min-content' : '';
    // Filter out empty strings and join
    return [infobarCol, viewerCol, sidebarCol].filter(Boolean).join(' ');
  }};

  /* Define rows - 1fr for main content, auto for gallery/timeline */
  grid-template-rows: ${({ $showGallery, $showTimeline }) => {
    let rows = '1fr';
    if ($showGallery) rows += ' auto';
    if ($showTimeline) rows += ' auto';
    return rows;
  }};

  /* Assign grid areas using class names */
  & > .file-info-bar {
    grid-area: infobar;
  }
  & > .viewer-area {
    grid-area: viewer;
    min-height: 0;
    overflow: auto;
  } // Ensure viewer can shrink
  & > .file-select-sidebar {
    grid-area: sidebar;
  }
  & > .gallery-section-wrapper {
    // Updated class name
    grid-area: gallery; // Use gallery grid area name
    overflow: hidden;
  } // Control overflow here
  & > .timeline-section-wrapper {
    grid-area: timeline;
    overflow: hidden; // Control overflow here
  }

  /* Media query for smaller screens - STACKING layout */
  @media (max-width: 768px) {
    grid-template-areas: ${({ $showInfobar, $showSidebar, $showGallery, $showTimeline }) => {
      let areas = '';
      if ($showInfobar) areas += '"infobar"\n';
      areas += '"viewer"\n';
      if ($showSidebar) areas += '"sidebar"\n';
      if ($showGallery) areas += '"gallery"\n';
      if ($showTimeline) areas += '"timeline" ';
      return areas.trim();
    }};
    grid-template-columns: 1fr; /* Single column */
    grid-template-rows: ${({ $showInfobar, $showSidebar, $showGallery, $showTimeline }) => {
      const rows = [];
      if ($showInfobar) rows.push('auto');
      rows.push('1fr'); // Viewer takes main space
      if ($showSidebar) rows.push('auto');
      if ($showGallery) rows.push('auto');
      if ($showTimeline) rows.push('auto');
      return rows.join(' ');
    }};

    /* Ensure gallery section wrapper handles its own overflow on mobile */
    & > .gallery-section-wrapper {
      // Updated class name
      // The inner ThumbnailCarousel controls mobile expansion/collapse
      // This wrapper just needs basic overflow handling if needed
      overflow: hidden;
    }
    & > .timeline-section-wrapper {
      overflow: hidden;
    }
  }
`;

const StyledViewerContainer = styled.div`
  min-height: 0; /* Important for flex/grid children */
  overflow: hidden;
  width: 100%;
  display: flex; /* Use flex to center spinner */
  align-items: center; /* Center vertically */
  justify-content: center; /* Center horizontally */
  position: relative; /* Needed for absolute positioning of arrows */
`;

const StyledSpinnerContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

// Copied NavArrowButton styles from PatientFilesViewModal
const NavArrowButton = styled(Button)<{ $position: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $position }) => ($position === 'left' ? 'left: 10px;' : 'right: 10px;')}
  z-index: 10; /* Ensure arrows are above the viewer content */
  background-color: rgba(0, 0, 0, 0.3);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover {
    background-color: rgba(0, 0, 0, 0.5);
  }

  &:disabled {
    background-color: rgba(0, 0, 0, 0.1);
    cursor: not-allowed;
  }
`;
